/**
 * 🖨 Dymo LabelWriter 550 — Impression directe via Dymo Connect Web Service
 *
 * Pré-requis sur le PC d'entrepôt :
 *   1. Brancher l'imprimante Dymo LabelWriter 550 en USB
 *   2. Installer Dymo Connect for Desktop (https://www.dymo.com/connect)
 *   3. Dymo Connect expose alors une API HTTP locale sur https://localhost:41951
 *   4. Charger des étiquettes 30252 Address (1⅛" × 3½" = 28 × 89 mm)
 *
 * 🆕 2026-02-21 — Architecture finale 3 niveaux :
 *   1. Framework officiel Dymo Connect JS (https://labelwriter.com/software/dls/sdk/...)
 *      → c'est lui qui gère correctement la traduction du XML vers ce que LW550
 *      attend (gère les ZIP .dymo en interne).
 *   2. Si le framework n'est pas chargeable (CSP, offline) → fallback HTTP direct
 *      en essayant les 2 formats XML (DesktopLabel puis DieCutLabel).
 *   3. Si TOUT échoue → ouverture d'une fenêtre d'aperçu PNG que l'utilisateur
 *      peut imprimer manuellement via le dialogue système (Ctrl+P → Dymo).
 */
import JsBarcode from 'jsbarcode';

const DYMO_BASE = 'https://localhost:41951/DYMO/DLS/Printing';
const DYMO_FRAMEWORK_URL = 'https://labelwriter.com/software/dls/sdk/js/DYMO.Label.Framework.latest.js';
// CDN unpkg en backup au cas où le CDN officiel Dymo est bloqué
const DYMO_FRAMEWORK_FALLBACK_URL = 'https://unpkg.com/dymo-connect-framework/dymo.connect.framework.js';

// 📐 2026-06-01 [Phase 29] — Format imprimante : 89 mm × 36 mm (LANDSCAPE).
//   L'imprimante imprime à l'horizontale ; l'étiquette est lue à plat
//   (texte horizontal, code-barres horizontal).
//   À 300 DPI : 89 mm = 1051 px ; 36 mm = 425 px.
const LABEL_89x36_LANDSCAPE = { widthPx: 1051, heightPx: 425, dpi: 300, widthMm: 89, heightMm: 36 };
// Alias rétro-compat pour les imports historiques
const LABEL_36x89_PORTRAIT = LABEL_89x36_LANDSCAPE;

// Legacy 30252 — gardé pour fallback uniquement (au cas où un poste utilise
// encore l'ancienne imprimante). Le code actif utilise désormais LABEL_36x89_PORTRAIT.
const LABEL_30252 = { widthPx: 1050, heightPx: 330, dpi: 300 };

let _frameworkPromise = null;
/** Charge dynamiquement le framework officiel Dymo. Idempotent. */
function loadDymoFramework() {
  if (typeof window !== 'undefined' && window.dymo?.label?.framework) {
    return Promise.resolve(window.dymo);
  }
  if (_frameworkPromise) return _frameworkPromise;
  _frameworkPromise = new Promise((resolve, reject) => {
    const tryUrl = (url, fallback) => {
      const s = document.createElement('script');
      s.src = url;
      s.async = true;
      s.onload = () => {
        if (window.dymo?.label?.framework) {
          try { window.dymo.label.framework.init(); } catch {}
          resolve(window.dymo);
        } else if (fallback) {
          tryUrl(fallback, null);
        } else {
          reject(new Error('Framework Dymo chargé mais non utilisable'));
        }
      };
      s.onerror = () => {
        if (fallback) tryUrl(fallback, null);
        else reject(new Error('Impossible de charger le framework Dymo'));
      };
      document.head.appendChild(s);
    };
    tryUrl(DYMO_FRAMEWORK_URL, DYMO_FRAMEWORK_FALLBACK_URL);
  });
  return _frameworkPromise;
}

export async function dymoIsAvailable() {
  try {
    const r = await fetch(`${DYMO_BASE}/StatusConnected`, { method: 'GET' });
    return r.ok;
  } catch {
    return false;
  }
}

export async function dymoListPrinters() {
  try {
    const r = await fetch(`${DYMO_BASE}/GetPrinters`, { method: 'GET' });
    if (!r.ok) return [];
    const xml = await r.text();
    const matches = [...xml.matchAll(/<Name>([^<]+)<\/Name>/g)];
    return matches.map((m) => m[1]);
  } catch {
    return [];
  }
}

/**
 * Génère un Canvas HTML5 portrait 36×89 mm avec :
 *   - Nom du produit en GROS au-dessus (auto-shrink + multi-ligne wrap)
 *   - Code-barres Code128 en dessous (occupe ~55% de la hauteur utile)
 * Retourne le canvas (à convertir en PNG ensuite).
 *
 * 🐛 2026-05-31 — Le nom est désormais TOUJOURS visible en entier :
 *   - auto-wrap sur plusieurs lignes selon la largeur disponible
 *   - auto-shrink de la police (max 64 → min 18) si trop de lignes
 */
function renderLabelCanvas({ productName, barcodeValue }) {
  // 🆕 [Phase 29] LANDSCAPE 89×36mm : layout en 2 colonnes
  //   Gauche : nom du produit (auto-shrink + multi-ligne wrap)
  //   Droite : code-barres horizontal
  const { widthPx, heightPx } = LABEL_89x36_LANDSCAPE;

  const canvas = document.createElement('canvas');
  canvas.width = widthPx;
  canvas.height = heightPx;
  const ctx = canvas.getContext('2d');

  // Fond blanc
  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, widthPx, heightPx);

  // --- Section gauche : Nom du produit (~50% largeur) ---
  const padX = 18;
  const padY = 16;
  const nameAreaW = Math.floor(widthPx * 0.50) - padX;
  const nameAreaH = heightPx - padY * 2;
  const nameAreaX = padX;
  const nameAreaY = padY;
  const maxTextWidth = nameAreaW - padX;

  ctx.fillStyle = '#000000';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  const safeName = String(productName || 'Produit').trim();

  const wrapLines = (text, fontPx) => {
    ctx.font = `bold ${fontPx}px Arial, sans-serif`;
    const words = text.split(/\s+/).filter(Boolean);
    if (words.length === 0) return [''];
    const lines = [];
    let current = '';
    for (const w of words) {
      const candidate = current ? `${current} ${w}` : w;
      if (ctx.measureText(candidate).width <= maxTextWidth) {
        current = candidate;
      } else {
        if (!current) {
          if (ctx.measureText(w).width > maxTextWidth) return null;
          lines.push(w);
          current = '';
        } else {
          lines.push(current);
          current = w;
          if (ctx.measureText(current).width > maxTextWidth) return null;
        }
      }
    }
    if (current) lines.push(current);
    return lines;
  };

  let fontSize = 64;
  let lines = wrapLines(safeName, fontSize);
  const lineHeightFactor = 1.15;
  while (
    fontSize > 18 &&
    (lines === null || lines.length * fontSize * lineHeightFactor > nameAreaH - 8)
  ) {
    fontSize -= 3;
    lines = wrapLines(safeName, fontSize);
  }
  if (!lines || lines.length === 0) lines = [safeName];

  ctx.font = `bold ${fontSize}px Arial, sans-serif`;
  const lineH = fontSize * lineHeightFactor;
  const totalH = lines.length * lineH;
  const startY = nameAreaY + (nameAreaH - totalH) / 2 + lineH / 2;
  const nameCenterX = nameAreaX + nameAreaW / 2;
  lines.forEach((ln, i) => {
    ctx.fillText(ln, nameCenterX, startY + i * lineH);
  });

  // --- Section droite : Code-barres horizontal (~50% largeur) ---
  const barcodeAreaX = nameAreaX + nameAreaW + 8;
  const barcodeAreaW = widthPx - barcodeAreaX - padX;
  const barcodeAreaH = nameAreaH;

  const barcodeCanvas = document.createElement('canvas');
  JsBarcode(barcodeCanvas, String(barcodeValue || ''), {
    format: 'CODE128',
    width: 3,
    height: 200,
    displayValue: true,
    fontSize: 28,
    margin: 4,
    background: '#FFFFFF',
    lineColor: '#000000',
  });

  const scale = Math.min(
    barcodeAreaW / barcodeCanvas.width,
    barcodeAreaH / barcodeCanvas.height,
  );
  const drawW = barcodeCanvas.width * scale;
  const drawH = barcodeCanvas.height * scale;
  const bcX = barcodeAreaX + (barcodeAreaW - drawW) / 2;
  const bcY = nameAreaY + (barcodeAreaH - drawH) / 2;
  ctx.drawImage(barcodeCanvas, bcX, bcY, drawW, drawH);

  return canvas;
}

/**
 * Construit le XML d'étiquette au nouveau format LW550 (Dymo Connect v1.x+).
 * Root : <DesktopLabel> au lieu du legacy <DieCutLabel>.
 * Pour 30252 Address (3.5" × 1.125"), dimensions en pouces.
 */
function buildDesktopLabelXml(pngDataUri) {
  const base64 = pngDataUri.replace(/^data:image\/png;base64,/, '');
  return `<?xml version="1.0" encoding="utf-8" standalone="no"?>
<DesktopLabel Version="1">
  <DYMOLabel Version="3">
    <Description>Address 30252</Description>
    <Orientation>Landscape</Orientation>
    <LabelName>Address30252</LabelName>
    <InitialLength>0</InitialLength>
    <BorderStyle>SolidLine</BorderStyle>
    <DYMORect>
      <DYMOPoint>
        <X>0</X>
        <Y>0</Y>
      </DYMOPoint>
      <Size>
        <Width>3.5</Width>
        <Height>1.125</Height>
      </Size>
    </DYMORect>
    <BorderColor><SolidColor Color="#000000"/></BorderColor>
    <BorderThickness>0</BorderThickness>
    <Show_Border>False</Show_Border>
    <DynamicLayoutManager>
      <RotationType>Rotation0</RotationType>
      <LabelObjects>
        <ImageObject>
          <Name>Image</Name>
          <Brushes>
            <BackgroundBrush><SolidColorBrush><Color A="1" R="1" G="1" B="1"></Color></SolidColorBrush></BackgroundBrush>
            <BorderBrush><SolidColorBrush><Color A="1" R="0" G="0" B="0"></Color></SolidColorBrush></BorderBrush>
            <StrokeBrush><SolidColorBrush><Color A="1" R="0" G="0" B="0"></Color></SolidColorBrush></StrokeBrush>
            <FillBrush><SolidColorBrush><Color A="0" R="1" G="1" B="1"></Color></SolidColorBrush></FillBrush>
          </Brushes>
          <Rotation>Rotation0</Rotation>
          <OutlineThickness>1</OutlineThickness>
          <IsOutlined>false</IsOutlined>
          <BorderStyle>SolidLine</BorderStyle>
          <Data>${base64}</Data>
          <ScaleMode>Uniform</ScaleMode>
          <HorizontalAlignment>Center</HorizontalAlignment>
          <VerticalAlignment>Middle</VerticalAlignment>
          <ObjectLayout>
            <DYMOPoint>
              <X>0</X>
              <Y>0</Y>
            </DYMOPoint>
            <Size>
              <Width>3.5</Width>
              <Height>1.125</Height>
            </Size>
          </ObjectLayout>
        </ImageObject>
      </LabelObjects>
    </DynamicLayoutManager>
  </DYMOLabel>
</DesktopLabel>`;
}

/**
 * Legacy XML format pour LW450/450 Turbo (Dymo Connect ancien / DLS SDK 8.x).
 * Gardé comme fallback si le nouveau format échoue.
 */
function buildLegacyDieCutLabelXml(pngDataUri) {
  const base64 = pngDataUri.replace(/^data:image\/png;base64,/, '');
  return `<?xml version="1.0" encoding="utf-8"?>
<DieCutLabel Version="8.0" Units="twips">
  <PaperOrientation>Landscape</PaperOrientation>
  <Id>Address</Id>
  <PaperName>30252 Address</PaperName>
  <DrawCommands>
    <RoundRectangle X="0" Y="0" Width="1581" Height="5040" Rx="270" Ry="270" />
  </DrawCommands>
  <ObjectInfo>
    <ImageObject>
      <Name>Image</Name>
      <ForeColor Alpha="255" Red="0" Green="0" Blue="0" />
      <BackColor Alpha="0" Red="255" Green="255" Blue="255" />
      <LinkedObjectName></LinkedObjectName>
      <Rotation>Rotation0</Rotation>
      <IsMirrored>False</IsMirrored>
      <IsVariable>False</IsVariable>
      <Image>${base64}</Image>
      <ScaleMode>Uniform</ScaleMode>
      <BorderColor Alpha="255" Red="0" Green="0" Blue="0" />
      <BorderThickness>0</BorderThickness>
      <HorizontalAlignment>Center</HorizontalAlignment>
      <VerticalAlignment>Middle</VerticalAlignment>
    </ImageObject>
    <Bounds X="0" Y="0" Width="5040" Height="1581" />
  </ObjectInfo>
</DieCutLabel>`;
}

/**
 * Template de label utilisé avec le framework officiel Dymo.
 * Le framework remplit lui-même l'`<Image>` (placeholder vide) via
 * `label.setObjectImage('IMAGE_1', base64)`.
 */
function buildLabelTemplateForFramework() {
  return `<?xml version="1.0" encoding="utf-8"?>
<DieCutLabel Version="8.0" Units="twips">
  <PaperOrientation>Landscape</PaperOrientation>
  <Id>Address</Id>
  <PaperName>30252 Address</PaperName>
  <DrawCommands>
    <RoundRectangle X="0" Y="0" Width="1581" Height="5040" Rx="270" Ry="270" />
  </DrawCommands>
  <ObjectInfo>
    <ImageObject>
      <Name>IMAGE_1</Name>
      <ForeColor Alpha="255" Red="0" Green="0" Blue="0" />
      <BackColor Alpha="0" Red="255" Green="255" Blue="255" />
      <LinkedObjectName></LinkedObjectName>
      <Rotation>Rotation0</Rotation>
      <IsMirrored>False</IsMirrored>
      <IsVariable>True</IsVariable>
      <Image></Image>
      <ScaleMode>Uniform</ScaleMode>
      <BorderColor Alpha="255" Red="0" Green="0" Blue="0" />
      <BorderThickness>0</BorderThickness>
      <HorizontalAlignment>Center</HorizontalAlignment>
      <VerticalAlignment>Middle</VerticalAlignment>
    </ImageObject>
    <Bounds X="0" Y="0" Width="5040" Height="1581" />
  </ObjectInfo>
</DieCutLabel>`;
}

/** Impression via framework officiel Dymo (méthode privilégiée — gère LW550). */
async function _printViaFramework({ printerName, pngDataUri }) {
  const dymo = await loadDymoFramework();
  const fw = dymo?.label?.framework;
  if (!fw) throw new Error('Framework Dymo non disponible');
  if (typeof fw.checkEnvironment === 'function') {
    try { fw.checkEnvironment(); } catch {}
  }
  const labelXml = buildLabelTemplateForFramework();
  const label = fw.openLabelXml(labelXml);
  const base64 = pngDataUri.replace(/^data:image\/png;base64,/, '');
  label.setObjectImage('IMAGE_1', base64);
  const printParamsXml = '<LabelWriterPrintParams><Copies>1</Copies></LabelWriterPrintParams>';
  // L'API moderne accepte un objet ou une chaîne. On essaie objet, fallback string.
  try {
    await fw.printLabel(printerName, printParamsXml, label.getLabelXml(), null);
  } catch (e) {
    // Certaines versions sont synchrones
    fw.printLabel(printerName, printParamsXml, label.getLabelXml(), null);
  }
  return true;
}

/** Impression via modale intégrée à la page (fallback ultime — compatible kiosque plein écran). */
function _printViaBrowserDialog({ pngDataUri, productName }) {
  // 1. Sortir du mode plein écran s'il est actif (sinon le dialogue Windows
  //    reste caché derrière et le kiosque semble gelé).
  if (typeof document !== 'undefined' && document.fullscreenElement) {
    try { document.exitFullscreen(); } catch {}
  }

  // 2. Évite les doublons si l'utilisateur clique 2 fois
  const existing = document.getElementById('dymo-print-overlay');
  if (existing) existing.remove();

  // 3. Construit une modale dans le document courant (visible même en kiosque)
  const overlay = document.createElement('div');
  overlay.id = 'dymo-print-overlay';
  overlay.style.cssText = [
    'position:fixed', 'inset:0', 'z-index:2147483647', 'background:rgba(15,23,42,0.86)',
    'display:flex', 'align-items:center', 'justify-content:center', 'padding:16px',
    'font-family:system-ui,-apple-system,sans-serif',
  ].join(';');

  const safeName = (productName || '').replace(/</g, '&lt;');
  overlay.innerHTML = `
    <div style="background:#fff;border-radius:12px;max-width:560px;width:100%;overflow:hidden;box-shadow:0 24px 60px rgba(0,0,0,0.45)">
      <div style="padding:14px 18px;border-bottom:1px solid #e5e7eb;display:flex;align-items:center;justify-content:space-between">
        <h2 style="margin:0;font-size:16px;color:#0f172a">🖨 Étiquette : ${safeName}</h2>
        <button id="dymo-close-btn" style="border:0;background:transparent;font-size:24px;cursor:pointer;color:#64748b;line-height:1">×</button>
      </div>
      <div style="padding:18px;background:#f8fafc;display:flex;justify-content:center">
        <img src="${pngDataUri}" alt="aperçu étiquette"
          style="width:280px;height:90px;display:block;border:1px dashed #cbd5e1;background:white" />
      </div>
      <div style="padding:16px 18px 18px">
        <p style="margin:0 0 12px;font-size:13px;color:#475569;line-height:1.5">
          Cliquez le bouton vert. Dans la fenêtre Windows qui s'ouvre, sélectionnez
          <strong>DYMO LabelWriter 550</strong> puis Imprimer.
        </p>
        <button id="dymo-do-print-btn"
          style="width:100%;padding:14px 16px;background:#16a34a;color:white;border:0;border-radius:8px;font-size:16px;font-weight:600;cursor:pointer;display:flex;align-items:center;justify-content:center;gap:8px">
          🖨 Ouvrir l'impression Windows
        </button>
        <p id="dymo-status" style="margin:10px 0 0;font-size:12px;color:#16a34a;text-align:center;display:none">
          ✅ Dialogue ouvert. Sélectionnez votre Dymo dans la liste.
        </p>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);

  const cleanup = () => { try { overlay.remove(); } catch {} };

  overlay.querySelector('#dymo-close-btn').addEventListener('click', cleanup);
  overlay.addEventListener('click', (e) => { if (e.target === overlay) cleanup(); });

  overlay.querySelector('#dymo-do-print-btn').addEventListener('click', () => {
    // 4. Iframe invisible : encapsule le label avec son @page CSS,
    //    sans polluer le DOM du kiosque, et déclenche le print isolé.
    const iframe = document.createElement('iframe');
    iframe.style.cssText = 'position:absolute;left:-99999px;top:-99999px;width:0;height:0;border:0';
    document.body.appendChild(iframe);
    const idoc = iframe.contentDocument;
    idoc.open();
    idoc.write(`<!DOCTYPE html><html><head>
<style>
  @page { size: 89mm 36mm; margin: 0; }
  html, body { margin: 0; padding: 0; background: white; }
  img { width: 89mm; height: 36mm; display: block; }
</style></head><body><img src="${pngDataUri}" alt="étiquette" /></body></html>`);
    idoc.close();
    overlay.querySelector('#dymo-status').style.display = 'block';
    setTimeout(() => {
      try {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
      } catch (e) { /* ignore */ }
      // Garde l'iframe pendant 2 secondes au cas où le dialogue est lent
      setTimeout(() => { try { document.body.removeChild(iframe); } catch {} }, 2500);
    }, 250);
  });

  return { method: 'inapp_modal' };
}

async function _sendToDymo(printerName, labelXml) {
  const params = new URLSearchParams();
  params.append('printerName', printerName);
  params.append('labelXml', labelXml);
  params.append('labelSetXml', '');
  params.append('printParamsXml', '');
  const r = await fetch(`${DYMO_BASE}/PrintLabel`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: params.toString(),
  });
  if (!r.ok) {
    const txt = await r.text().catch(() => '');
    return { ok: false, status: r.status, body: txt };
  }
  return { ok: true };
}

export async function dymoPrintLabel({ printerName, productName, barcodeValue }) {
  const canvas = renderLabelCanvas({ productName, barcodeValue });
  const pngDataUri = canvas.toDataURL('image/png');

  const errors = [];

  // 🥇 Méthode 1 — Framework officiel Dymo (gère LW550 + LW450)
  try {
    await _printViaFramework({ printerName, pngDataUri });
    return { method: 'framework' };
  } catch (e) {
    errors.push(`Framework: ${e?.message || e}`);
  }

  // 🥈 Méthode 2 — POST HTTP direct, format DesktopLabel puis DieCutLabel
  for (const xmlBuilder of [buildDesktopLabelXml, buildLegacyDieCutLabelXml]) {
    const res = await _sendToDymo(printerName, xmlBuilder(pngDataUri));
    if (res.ok) return { method: 'http_xml' };
    errors.push(`${xmlBuilder.name}: ${res.status} ${String(res.body || '').slice(0, 120)}`);
  }

  // 🥉 Méthode 3 — Fallback aperçu navigateur (l'utilisateur clique Imprimer)
  try {
    return _printViaBrowserDialog({ pngDataUri, productName });
  } catch (e) {
    errors.push(`Aperçu navigateur: ${e?.message || e}`);
  }

  throw new Error(`Toutes les méthodes d'impression ont échoué :\n${errors.join('\n')}`);
}

export async function dymoPrintLabelAuto({ productName, barcodeValue }) {
  const available = await dymoIsAvailable();
  if (!available) {
    throw new Error(
      "Dymo Connect n'est pas détecté sur ce poste. " +
        "Installez Dymo Connect for Desktop puis branchez l'imprimante."
    );
  }
  const printers = await dymoListPrinters();
  if (printers.length === 0) {
    throw new Error("Aucune imprimante Dymo branchée. Vérifiez le câble USB et l'alimentation.");
  }
  return dymoPrintLabel({ printerName: printers[0], productName, barcodeValue });
}

/**
 * 🧪 Helper de DEBUG : ouvre la prévisualisation du canvas dans un nouvel onglet
 * pour voir EXACTEMENT ce qui sera envoyé à l'imprimante. Utile si l'étiquette
 * sort vide ou mal formée.
 */
export function dymoPreviewLabel({ productName, barcodeValue }) {
  const canvas = renderLabelCanvas({ productName, barcodeValue });
  const dataUri = canvas.toDataURL('image/png');
  const win = window.open();
  if (win) {
    win.document.write(
      `<html><head><title>Aperçu étiquette</title></head>
       <body style="background:#333;display:flex;align-items:center;justify-content:center;height:100vh;margin:0">
       <img src="${dataUri}" style="border:2px solid #f59e0b;background:white;width:144px;height:356px" />
       </body></html>`
    );
  }
}

/**
 * 🛢️ [15] Étiquette "Prochain changement d'huile" (format Dymo 36×89 mm).
 *
 * Spec :
 *   Titre   : PROCHAIN CHANGEMENT D'HUILE (gras)
 *   Valeur  : "À : 120 000 km" (très gros, lisible de loin — info principale)
 *   Footer  : Unité + Effectué le DD/MM/YYYY (petites lignes)
 *   Pas de code-barres (étiquette pas scannée, juste lue).
 *
 * Méthode : compose le contenu dans un canvas portrait 425×1051px puis
 * envoie via la même pipeline browser que les autres étiquettes (@page 36×89mm).
 *
 * @param {{ uniteNom: string, prochainKm: number, dateChangement: string }} payload
 *   dateChangement : ISO 'YYYY-MM-DD' ou format français déjà préparé.
 */
export function dymoPrintHuileLabel({ uniteNom, prochainKm, dateChangement }) {
  if (typeof prochainKm !== 'number' || prochainKm <= 0) {
    throw new Error('prochainKm doit être un nombre strictement positif');
  }
  // 🆕 [Phase 29] Format LANDSCAPE 89×36mm cohérent avec le reste
  const widthPx = 1051, heightPx = 425;
  const canvas = document.createElement('canvas');
  canvas.width = widthPx; canvas.height = heightPx;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, widthPx, heightPx);
  ctx.fillStyle = '#000000';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  // Titre : "PROCHAIN CHANGEMENT D'HUILE" — wrappable sur 2 lignes
  ctx.font = `bold 38px Arial, sans-serif`;
  const titleLines = ['PROCHAIN CHANGEMENT', "D'HUILE"];
  titleLines.forEach((ln, i) => ctx.fillText(ln, widthPx / 2, 110 + i * 50));

  // Séparateur fin
  ctx.fillRect(40, 235, widthPx - 80, 2);

  // Bloc principal : "À : XXX XXX km" — très gros
  ctx.font = `bold 56px Arial, sans-serif`;
  ctx.fillText('À :', widthPx / 2, 320);
  const kmFormatted = prochainKm.toLocaleString('fr-CA');
  // Auto-shrink si le nombre est trop large (ex: 1 234 567 km)
  let kmFont = 130;
  do {
    ctx.font = `bold ${kmFont}px Arial, sans-serif`;
    if (ctx.measureText(`${kmFormatted} km`).width <= widthPx - 40) break;
    kmFont -= 6;
  } while (kmFont > 50);
  ctx.fillText(`${kmFormatted} km`, widthPx / 2, 470);

  // Séparateur
  ctx.fillRect(40, 620, widthPx - 80, 2);

  // Footer : Unité + Date
  ctx.font = `bold 32px Arial, sans-serif`;
  ctx.fillText('Unité :', widthPx / 2, 700);
  // Auto-shrink unité longue
  let uFont = 36;
  do {
    ctx.font = `${uFont}px Arial, sans-serif`;
    if (ctx.measureText(uniteNom || '—').width <= widthPx - 40) break;
    uFont -= 2;
  } while (uFont > 16);
  ctx.fillText(uniteNom || '—', widthPx / 2, 760);

  ctx.font = `bold 28px Arial, sans-serif`;
  ctx.fillText('Effectué le :', widthPx / 2, 860);
  ctx.font = `28px Arial, sans-serif`;
  // Format : si ISO yyyy-mm-dd → reformater DD/MM/YYYY
  let dateFmt = dateChangement || '';
  const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateChangement || '');
  if (iso) dateFmt = `${iso[3]}/${iso[2]}/${iso[1]}`;
  ctx.fillText(dateFmt || '—', widthPx / 2, 920);

  // Envoi à l'impression via iframe (même mécanique que dymoBulkPrintLabels)
  const dataUri = canvas.toDataURL('image/png');
  const iframe = document.createElement('iframe');
  iframe.style.cssText = 'position:absolute;left:-99999px;top:-99999px;width:0;height:0;border:0';
  document.body.appendChild(iframe);
  const idoc = iframe.contentDocument;
  idoc.open();
  idoc.write(`<!DOCTYPE html><html><head>
<style>
  @page { size: 89mm 36mm; margin: 0; }
  html, body { margin: 0; padding: 0; background: white; }
  img { width: 89mm; height: 36mm; display: block; }
</style></head><body><img src="${dataUri}" alt="étiquette changement d'huile" /></body></html>`);
  idoc.close();
  setTimeout(() => {
    try { iframe.contentWindow.focus(); iframe.contentWindow.print(); } catch {}
    setTimeout(() => { try { document.body.removeChild(iframe); } catch {} }, 4000);
  }, 300);
  return { method: 'huile_label_dymo_89x36' };
}

/**
 * 🖨 [Phase v13.60.10 — 2026-08-15] Impression d'une étiquette de TEXTE LIBRE
 *   (format Dymo 89×36 mm LANDSCAPE, même pipeline iframe que les autres
 *   étiquettes — sans code-barres, texte gros centré multi-ligne).
 *
 * Cas d'usage typique : coller une note sur une caisse ("À livrer lundi",
 * "Retour fournisseur", "Ne pas ouvrir", etc.) directement depuis l'entrepôt.
 *
 * Spec :
 *   - Texte auto-shrink (max 160px → min 24px) + auto-wrap sur 1-3 lignes.
 *   - Les sauts de ligne \n saisis par l'utilisateur sont respectés.
 *   - Aucune dépendance à Dymo Connect : passe par le dialogue d'impression
 *     natif du navigateur (l'utilisateur choisit sa Dymo LabelWriter 550).
 *   - Retourne { method, fontSize, lines } pour tests unitaires + debug.
 *
 * @param {{ text: string }} params
 * @returns {{ method: string, fontSize: number, lines: number }}
 */
export function dymoPrintFreeText({ text }) {
  const safe = String(text || '').trim();
  if (!safe) {
    throw new Error("Texte requis pour l'impression");
  }

  const { widthPx, heightPx } = LABEL_89x36_LANDSCAPE;
  const canvas = document.createElement('canvas');
  canvas.width = widthPx;
  canvas.height = heightPx;
  const ctx = canvas.getContext('2d');

  // Fond blanc
  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, widthPx, heightPx);

  ctx.fillStyle = '#000000';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  const padX = 40;
  const padY = 30;
  const maxTextWidth = widthPx - padX * 2;
  const maxTextHeight = heightPx - padY * 2;

  // Word-wrap qui respecte aussi les \n saisis par l'utilisateur
  const wrapLines = (input, fontPx) => {
    ctx.font = `bold ${fontPx}px Arial, sans-serif`;
    const paragraphs = String(input).split(/\r?\n/);
    const lines = [];
    for (const para of paragraphs) {
      const words = para.split(/\s+/).filter(Boolean);
      if (words.length === 0) {
        // Ligne vide utilisateur → on la conserve pour spacing
        lines.push('');
        continue;
      }
      let current = '';
      for (const w of words) {
        const candidate = current ? `${current} ${w}` : w;
        if (ctx.measureText(candidate).width <= maxTextWidth) {
          current = candidate;
        } else if (!current) {
          if (ctx.measureText(w).width > maxTextWidth) return null;
          lines.push(w);
          current = '';
        } else {
          lines.push(current);
          current = w;
          if (ctx.measureText(current).width > maxTextWidth) return null;
        }
      }
      if (current) lines.push(current);
    }
    return lines;
  };

  // Auto-shrink : on commence TRÈS gros et on réduit jusqu'à ce que tout tienne
  const lineHeightFactor = 1.2;
  let fontSize = 160;
  let lines = wrapLines(safe, fontSize);
  while (
    fontSize > 24 &&
    (lines === null || lines.length * fontSize * lineHeightFactor > maxTextHeight)
  ) {
    fontSize -= 4;
    lines = wrapLines(safe, fontSize);
  }
  if (!lines || lines.length === 0) lines = [safe];

  ctx.font = `bold ${fontSize}px Arial, sans-serif`;
  const lineH = fontSize * lineHeightFactor;
  const totalH = lines.length * lineH;
  const startY = padY + (maxTextHeight - totalH) / 2 + lineH / 2;
  const centerX = widthPx / 2;
  lines.forEach((ln, i) => ctx.fillText(ln, centerX, startY + i * lineH));

  // Envoi à l'impression via iframe (identique à dymoBulkPrintLabels)
  const dataUri = canvas.toDataURL('image/png');
  if (typeof document !== 'undefined' && document.fullscreenElement) {
    try { document.exitFullscreen(); } catch { /* no-op */ }
  }
  const iframe = document.createElement('iframe');
  iframe.style.cssText = 'position:absolute;left:-99999px;top:-99999px;width:0;height:0;border:0';
  document.body.appendChild(iframe);
  const idoc = iframe.contentDocument;
  idoc.open();
  idoc.write(`<!DOCTYPE html><html><head>
<style>
  @page { size: 89mm 36mm; margin: 0; }
  html, body { margin: 0; padding: 0; background: white; }
  img { width: 89mm; height: 36mm; display: block; }
</style></head><body><img src="${dataUri}" alt="étiquette texte libre" /></body></html>`);
  idoc.close();
  setTimeout(() => {
    try { iframe.contentWindow.focus(); iframe.contentWindow.print(); } catch { /* ignore */ }
    setTimeout(() => { try { document.body.removeChild(iframe); } catch { /* ignore */ } }, 4000);
  }, 300);

  return { method: 'freetext_dymo_89x36', fontSize, lines: lines.length };
}

/**
 * 🖨 2026-05-31 — Impression EN LOT (bulk) au format 36×89mm.
 *
 * Compile N étiquettes dans une SEULE iframe avec une page par étiquette
 * (CSS `page-break-after: always`), puis déclenche le dialogue d'impression
 * du navigateur. L'utilisateur choisit son imprimante Dymo et tout sort
 * d'un coup.
 *
 * Cette méthode est universelle (n'a pas besoin de Dymo Connect) et marche
 * pour 1 ou 1000 étiquettes. C'est notre méthode de référence pour le bulk.
 *
 * @param {Array<{productName: string, barcodeValue: string}>} items
 */
export function dymoBulkPrintLabels(items) {
  if (!Array.isArray(items) || items.length === 0) {
    throw new Error('Aucune étiquette à imprimer');
  }
  if (typeof document !== 'undefined' && document.fullscreenElement) {
    try { document.exitFullscreen(); } catch {}
  }

  const pages = items.map(it => {
    const c = renderLabelCanvas({ productName: it.productName, barcodeValue: it.barcodeValue });
    return c.toDataURL('image/png');
  });

  const iframe = document.createElement('iframe');
  iframe.style.cssText = 'position:absolute;left:-99999px;top:-99999px;width:0;height:0;border:0';
  document.body.appendChild(iframe);
  const idoc = iframe.contentDocument;
  idoc.open();
  idoc.write(`<!DOCTYPE html><html><head>
<style>
  @page { size: 89mm 36mm; margin: 0; }
  html, body { margin: 0; padding: 0; background: white; }
  .label { width: 89mm; height: 36mm; display: block; page-break-after: always; }
  .label:last-child { page-break-after: auto; }
  img { width: 89mm; height: 36mm; display: block; }
</style></head><body>
${pages.map((src, i) => `<div class="label"><img src="${src}" alt="étiquette ${i+1}" /></div>`).join('')}
</body></html>`);
  idoc.close();

  setTimeout(() => {
    try {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    } catch (e) { /* ignore */ }
    setTimeout(() => { try { document.body.removeChild(iframe); } catch {} }, 4000);
  }, 300);

  return { method: 'bulk_browser', count: items.length };
}
